import { zodResolver } from '@hookform/resolvers/zod';
import { BRAND, pageTitle } from '@palaccount/config';
import { SignUpRequestSchema } from '@palaccount/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { CreditCard, Headset, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';

import { api } from '@/api';
import { BrandMark } from '@/components/brand-mark';
import { LanguageSwitch } from '@/components/language-switch';
import { PasswordInput } from '@/components/password-input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { markSessionSeen, ME_QUERY_KEY, serverMessageKey } from '@/lib/session';
import { useUiStore } from '@/lib/ui-store';
import { cn } from '@/lib/utils';

/**
 * GEN-S2 §5 fields 5–7, validated client-side and NEVER transmitted: billing
 * is deferred scope (see the deviation note on SignUpRequestSchema), so the
 * card is neither charged (BR-GEN-S2-03, trivially) nor stored — the CVC in
 * particular never leaves this component (BR-GEN-S2-05). Messages are i18n
 * keys, translated at render.
 */
const CardDetailsSchema = z.object({
  cardNumber: z
    .string()
    .regex(/^\d{4} \d{4} \d{4} \d{4}$/, 'auth.errCardNumber')
    /**
     * ERR-GEN-S2-02 — "Your bank refused that card. Try another card." — the
     * one sliver of it that exists WITHOUT a bank: §15's Zero edge says "a
     * card number of all zeros is refused by `ERR-GEN-S2-02`", and all-zeros
     * is a shape no issuer has ever printed, checkable right here like the
     * expired month below. The sentence is the spec's, even though no bank
     * was asked, because the spec itself binds this case to this rule id.
     * The GENUINE bank-refusal path — a real issuer declining a real card —
     * cannot exist until a payment provider is wired (R21 route (a),
     * DECISION.md §8.7) and is recorded as blocked, not built.
     */
    .refine((value) => value !== '0000 0000 0000 0000', 'auth.errCardRefused'),
  expiry: z
    .string()
    .regex(/^(0[1-9]|1[0-2]) \/ \d{2}$/, 'auth.errCardExpired')
    // BR-GEN-S2-10 / ERR-GEN-S2-03: an expiry month in the past is refused.
    .refine((value) => {
      const [month, year] = value.split(' / ').map(Number);
      if (!month || year === undefined) return false;
      const now = new Date();
      const currentYear = now.getFullYear() % 100;
      const currentMonth = now.getMonth() + 1;
      return year > currentYear || (year === currentYear && month >= currentMonth);
    }, 'auth.errCardExpired'),
  // ERR-GEN-S2-04: exactly 3 digits.
  cvc: z.string().regex(/^\d{3}$/, 'auth.errCvc'),
});

/** The contract schema (what the server validates) plus the card-only extras. */
const SignUpFormSchema = SignUpRequestSchema.extend(CardDetailsSchema.shape);

type FormInput = z.input<typeof SignUpFormSchema>;
type FormOutput = z.output<typeof SignUpFormSchema>;

type Plan = 'starter' | 'business' | 'enterprise';

/** GEN-S2 §5 field 5: groups of four, the system adds the spaces. */
function maskCardNumber(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 16);
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
}

/** GEN-S2 §5 field 6: `MM / YY`, with an impossible month unpreventable to type. */
function maskExpiry(raw: string): string {
  let digits = raw.replace(/\D/g, '').slice(0, 4);
  if (digits.length >= 1 && Number(digits[0]) > 1) digits = `0${digits}`;
  if (digits.length >= 2 && Number(digits.slice(0, 2)) > 12) digits = `0${digits[0]}`;
  return digits.length > 2 ? `${digits.slice(0, 2)} / ${digits.slice(2)}` : digits;
}

/**
 * GEN-S2 — Create your account. One centered column, never wider than 760px;
 * two tabs that are the two purchase paths. "Pay by card" opens chosen, the
 * Business plan pre-selected and badged POPULAR (§7 "starting positions", not
 * settings). Switching tabs keeps typed values (§15): the card form stays
 * mounted and is hidden, not unmounted.
 *
 * Two recorded deviations, both inherited from the auth contract (its header
 * documents them):
 *  - A password box exists here. The spec sets the password via the activation
 *    email (BR-GEN-S2-08/09), which needs the email provider that lands at
 *    deploy; until then the API takes the password directly.
 *  - On success the new Owner is signed straight in rather than shown the
 *    "check your email" panel, for the same reason.
 * BR-GEN-S2-11 holds: the Talk-to-us tab sends nothing anywhere.
 */
export function SignUpPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'card' | 'call'>('card');
  const [plan, setPlan] = useState<Plan>('business');
  const [serverError, setServerError] = useState<string | null>(null);
  /**
   * R21 — the company was created but has no subscription yet. Set to the
   * server's own sentence about how to get one; null means the ordinary path.
   */
  const [awaiting, setAwaiting] = useState<string | null>(null);
  const locale = useUiStore((state) => state.locale);

  useEffect(() => {
    document.title = pageTitle(t('auth.createAccount'));
  }, [t]);

  const form = useForm<FormInput, unknown, FormOutput>({
    resolver: zodResolver(SignUpFormSchema),
    defaultValues: {
      fullName: '',
      workEmail: '',
      companyName: '',
      password: '',
      cardNumber: '',
      expiry: '',
      cvc: '',
    },
    mode: 'onSubmit',
  });

  const watched = form.watch(['fullName', 'workEmail', 'companyName', 'password', 'cardNumber', 'expiry', 'cvc']);
  const submitting = form.formState.isSubmitting;
  // BR-GEN-S2-01 — greyed out until every required box contains a value. The
  // grey-on-first-click also covers §15's duplicate double-click case.
  const canSubmit = watched.every((value) => Boolean(value)) && !submitting;

  const errors = form.formState.errors;

  const onSubmit = form.handleSubmit(async (values) => {
    setServerError(null);
    try {
      // Only the contract fields travel; card details never leave the page.
      const response = await api.auth.signUp({
        body: {
          fullName: values.fullName,
          workEmail: values.workEmail,
          companyName: values.companyName,
          password: values.password,
          // The switcher on THIS screen, travelling with the request. It is
          // the one moment on any path where the sender and the recipient of
          // an email are the same human, so it is the only place the
          // recipient's language can be learned rather than guessed — and it
          // becomes the company's, which every later invitation inherits.
          language: locale,
        },
      });
      if (response.status === 201) {
        // ── R21: SIGNING UP IS NOT THE SAME AS GETTING ACCESS ─────────────
        //
        // The company now exists — every seed, every role, the Owner's
        // account. What it may not have is a SUBSCRIPTION, because R21 removed
        // the trial entitlements that used to be granted here silently and the
        // online-payment route cannot complete until a provider is wired
        // (DECISION.md §8.7). The server says which, and the screen must not
        // guess: signing in with no period would be refused, and bouncing the
        // new Owner to a sign-in page that then rejects them reads as "your
        // signup failed" when in fact nothing failed at all.
        if (!response.body.subscriptionActive) {
          setAwaiting(response.body.message ?? null);
          return;
        }

        // The activation email is queued (BR-GEN-S2-08/09) but is not a gate
        // yet — REQUIRE_EMAIL_VERIFICATION is off until a real provider is
        // configured, because with the mock provider no activation mail can
        // arrive. So the freshly created Owner signs straight in.
        const signedIn = await api.auth.signIn({
          body: { email: values.workEmail, password: values.password, rememberMe: false },
        });
        if (signedIn.status === 200) {
          markSessionSeen();
          await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
          void navigate('/', { replace: true });
        } else {
          void navigate('/sign-in', { replace: true });
        }
        return;
      }
      // 409 (ERR-GEN-S2-01) or 429 — contract-fixed wording, translated when
      // known, passed through when not.
      const message = response.body.message;
      const key = serverMessageKey(message);
      setServerError(key ? t(key) : message);
    } catch {
      // ERR-GEN-S2-06 — the network died while creating.
      setServerError(t('auth.errSignupFailed'));
    }
  });

  const plans: Array<{ id: Plan; nameKey: string; price: string | null; seatsKey: string }> = [
    { id: 'starter', nameKey: 'auth.planStarter', price: '$29', seatsKey: 'auth.planStarterSeats' },
    { id: 'business', nameKey: 'auth.planBusiness', price: '$99', seatsKey: 'auth.planBusinessSeats' },
    { id: 'enterprise', nameKey: 'auth.planEnterprise', price: null, seatsKey: 'auth.planEntSeats' },
  ];

  return (
    <div className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto w-full max-w-[760px]">
        <div className="relative mb-2 flex items-center justify-center">
          <BrandMark />
          <LanguageSwitch className="absolute end-0" />
        </div>
        <h1 className="text-center text-2xl font-bold">{t('auth.createAccount')}</h1>
        <p className="mb-6 mt-1 text-center text-sm text-muted-foreground">{t('auth.createSub')}</p>

        {/* The two purchase paths (§8): a 460px grey track, two pill buttons. */}
        <div className="mx-auto mb-6 grid w-full max-w-[460px] grid-cols-2 gap-1 rounded-lg bg-muted p-1">
          <button
            type="button"
            onClick={() => setTab('card')}
            aria-pressed={tab === 'card'}
            className={cn(
              'flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              tab === 'card' ? 'bg-card shadow-sm' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <CreditCard className="size-4" aria-hidden />
            {t('auth.tabCard')}
          </button>
          <button
            type="button"
            onClick={() => setTab('call')}
            aria-pressed={tab === 'call'}
            className={cn(
              'flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              tab === 'call' ? 'bg-card shadow-sm' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Headset className="size-4" aria-hidden />
            {t('auth.tabCall')}
          </button>
        </div>

        {/* R21 — the company exists and cannot be signed into yet. It replaces
            the form rather than sitting above it, because there is nothing
            left to fill in and re-submitting would only collide with the
            account just created (ERR-GEN-S2-01). */}
        {awaiting !== null ? (
          <Card className="rounded-[18px]">
            <CardContent className="flex flex-col items-center gap-4 text-center">
              <span
                aria-hidden
                className="flex size-[62px] items-center justify-center rounded-xl bg-primary/10 text-primary"
              >
                <Headset className="size-7" />
              </span>
              <h2 className="text-lg font-semibold">{t('auth.awaitingSubTitle')}</h2>
              <p className="max-w-md text-sm text-muted-foreground">{t('auth.awaitingSubBody')}</p>
              {/* The server's own sentence, verbatim. It names the alternative
                  route — phone, cash, transfer — which is a real one that
                  works today, not an apology. */}
              <p role="status" className="max-w-md text-sm">
                {awaiting}
              </p>
              <p dir="ltr" className="font-mono text-2xl font-semibold text-primary">
                +1 (800) 555-0182
              </p>
              <Link to="/sign-in" className="text-sm font-medium text-primary hover:underline">
                {t('auth.signInLink')}
              </Link>
            </CardContent>
          </Card>
        ) : null}

        {/* The card path. Hidden — never unmounted — on the other tab, so
            switching keeps everything already typed (§15). Once the company
            EXISTS it is unmounted for real: §15's reason to keep it is that
            the person may come back to it, and after a successful signup they
            must not, because a second submit collides with the account they
            just created (ERR-GEN-S2-01). */}
        {awaiting === null ? (
        <Card className={cn('rounded-[18px]', tab !== 'card' && 'hidden')}>
          <CardContent>
            <form noValidate onSubmit={onSubmit} className="grid gap-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="fullName">{t('auth.fullName')}</Label>
                  <Input
                    id="fullName"
                    autoComplete="name"
                    placeholder={t('auth.fullNamePh')}
                    maxLength={120}
                    {...form.register('fullName')}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="workEmail">{t('auth.workEmail')}</Label>
                  <Input
                    id="workEmail"
                    type="email"
                    autoComplete="email"
                    placeholder={t('auth.emailPh')}
                    dir="ltr"
                    aria-invalid={Boolean(errors.workEmail)}
                    {...form.register('workEmail')}
                  />
                  {errors.workEmail ? (
                    <p className="text-sm text-destructive">{t('auth.errEmailFormat')}</p>
                  ) : null}
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="companyName">{t('auth.companyName')}</Label>
                {/* §15: Arabic company names are accepted and shown RTL — the
                    input direction follows the document, no forced dir. */}
                <Input
                  id="companyName"
                  autoComplete="organization"
                  placeholder={t('auth.companyNamePh')}
                  maxLength={120}
                  aria-invalid={Boolean(errors.companyName)}
                  {...form.register('companyName')}
                />
                {/* ERR-GEN-S2-05 — "Type your company name." The §14 row's
                    literal case (box left empty) never reaches a submit,
                    because BR-GEN-S2-01 greys the button first; what does
                    reach it is a name of only spaces, which Boolean() lets
                    through the grey-out and the contract's trim().min(1)
                    refuses here with the exact sentence. */}
                {errors.companyName ? (
                  <p className="text-sm text-destructive">{t('auth.errCompanyEmpty')}</p>
                ) : null}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="password">{t('auth.passwordLabel')}</Label>
                <PasswordInput id="password" autoComplete="new-password" {...form.register('password')} />
                {errors.password ? (
                  <p className="text-sm text-destructive">{t('auth.errPasswordRule')}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">{t('auth.passwordHint')}</p>
                )}
              </div>

              <fieldset className="grid gap-2">
                <legend className="mb-2 text-sm font-medium">{t('auth.choosePlan')}</legend>
                <div className="grid gap-3 sm:grid-cols-3">
                  {plans.map((entry) => (
                    <button
                      key={entry.id}
                      type="button"
                      onClick={() => setPlan(entry.id)}
                      aria-pressed={plan === entry.id}
                      className={cn(
                        'relative rounded-lg border p-4 text-start transition-colors',
                        plan === entry.id
                          ? 'border-2 border-primary bg-primary/5'
                          : 'border-border hover:border-primary/40',
                      )}
                    >
                      {entry.id === 'business' ? (
                        <span className="absolute -top-2.5 end-3 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground">
                          {t('auth.popular')}
                        </span>
                      ) : null}
                      <span className="block text-sm font-semibold">{t(entry.nameKey)}</span>
                      <span className="mt-1 block text-lg font-bold">
                        {/* Prices are always US dollars, whatever the language (§15). */}
                        {entry.price ? (
                          <>
                            <span dir="ltr">{entry.price}</span>
                            <span className="text-xs font-normal text-muted-foreground">{t('auth.perMo')}</span>
                          </>
                        ) : (
                          t('auth.priceCustom')
                        )}
                      </span>
                      <span className="mt-1 block text-xs text-muted-foreground">{t(entry.seatsKey)}</span>
                    </button>
                  ))}
                </div>
              </fieldset>

              {/* The payment row: 2 : 1 : 1, monospace figures. */}
              <div className="grid gap-3 sm:grid-cols-[2fr_1fr_1fr]">
                <div className="grid gap-2">
                  <Label htmlFor="cardNumber">{t('auth.cardNumber')}</Label>
                  <Input
                    id="cardNumber"
                    inputMode="numeric"
                    autoComplete="cc-number"
                    placeholder="1234 5678 9012 3456"
                    dir="ltr"
                    className="font-mono"
                    aria-invalid={Boolean(errors.cardNumber)}
                    {...form.register('cardNumber', {
                      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                        form.setValue('cardNumber', maskCardNumber(event.target.value));
                      },
                    })}
                  />
                  {errors.cardNumber?.message ? (
                    <p className="text-sm text-destructive">{t(errors.cardNumber.message)}</p>
                  ) : null}
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="expiry">{t('auth.expiry')}</Label>
                  <Input
                    id="expiry"
                    inputMode="numeric"
                    autoComplete="cc-exp"
                    placeholder="08 / 29"
                    dir="ltr"
                    className="font-mono"
                    aria-invalid={Boolean(errors.expiry)}
                    {...form.register('expiry', {
                      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                        form.setValue('expiry', maskExpiry(event.target.value));
                      },
                    })}
                  />
                  {errors.expiry?.message ? (
                    <p className="text-sm text-destructive">{t(errors.expiry.message)}</p>
                  ) : null}
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="cvc">{t('auth.cvc')}</Label>
                  <Input
                    id="cvc"
                    inputMode="numeric"
                    autoComplete="cc-csc"
                    placeholder="123"
                    dir="ltr"
                    className="font-mono"
                    maxLength={3}
                    aria-invalid={Boolean(errors.cvc)}
                    {...form.register('cvc', {
                      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                        form.setValue('cvc', event.target.value.replace(/\D/g, '').slice(0, 3));
                      },
                    })}
                  />
                  {errors.cvc?.message ? (
                    <p className="text-sm text-destructive">{t(errors.cvc.message)}</p>
                  ) : null}
                </div>
              </div>

              {serverError ? (
                <p role="alert" className="text-sm text-destructive">
                  {serverError}
                </p>
              ) : null}

              <Button type="submit" disabled={!canSubmit} className="h-[50px] w-full text-base">
                {submitting ? <Loader2 className="animate-spin" aria-label={t('auth.creating')} /> : null}
                {t('auth.startTrial')}
              </Button>
              {/* BR-GEN-S2-03: shown under the button, and true — nothing is
                  charged (nothing is even transmitted). */}
              <p className="text-center text-xs text-muted-foreground">{t('auth.noChargeToday')}</p>
            </form>
          </CardContent>
        </Card>
        ) : null}

        {/* The assisted path — provisioning by a Super Admin. Nothing to fill
            in, nothing sent anywhere (BR-GEN-S2-11). */}
        {tab === 'call' && awaiting === null ? (
          <Card className="rounded-[18px]">
            <CardContent className="flex flex-col items-center gap-4 text-center">
              <span
                aria-hidden
                className="flex size-[62px] items-center justify-center rounded-xl bg-primary/10 text-primary"
              >
                <Headset className="size-7" />
              </span>
              <h2 className="text-lg font-semibold">{t('auth.talkTitle')}</h2>
              <p className="max-w-md text-sm text-muted-foreground">
                {t('auth.talkP', { brand: BRAND.name })}
              </p>
              <p dir="ltr" className="font-mono text-2xl font-semibold text-primary">
                +1 (800) 555-0182
              </p>
              <p className="text-xs text-muted-foreground">{t('auth.callHours')}</p>
              <ol className="mt-2 grid w-full max-w-md gap-3 text-start">
                {([1, 2, 3] as const).map((step) => (
                  <li key={step} className="flex items-start gap-3">
                    <span
                      aria-hidden
                      className="mt-0.5 flex size-[26px] shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground"
                    >
                      {step}
                    </span>
                    <span>
                      <span className="block text-sm font-semibold">{t(`auth.step${step}T`)}</span>
                      <span className="block text-xs text-muted-foreground">{t(`auth.step${step}S`)}</span>
                    </span>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        ) : null}

        {/* Outside both tabs, shown under either (§8). */}
        <p className="mt-6 text-center text-sm text-muted-foreground">
          {t('auth.alreadyHave')}{' '}
          <Link to="/sign-in" className="font-medium text-primary hover:underline">
            {t('auth.signInLink')}
          </Link>
        </p>
      </div>
    </div>
  );
}
